import { Component, inject, OnDestroy, OnInit, Signal, ChangeDetectorRef } from '@angular/core';

import {concatMap, of, Subscription, switchMap, interval} from 'rxjs';
import { DatePipe } from '@angular/common';

import { SessionStorageService } from '../../../core/services/session-storage.service';
import { LoginService } from '../../../core/services/login.service';
import { SurveyService } from 'src/app/core/services/surveys.service';
import { SidebarService } from '../../../core/services/sidebar.service';
import { environment } from '../../../../environments/environment';
import { SSOCredentials } from '../../../core/models/credentials.model';
import { Router } from '@angular/router';
import { faDesktop, faGear, faRightFromBracket, faComments, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { ManagementUser } from '../../../core/models/management-user.model';
import { LogoutReason, SessionTimerService } from 'src/app/core/services/session-timer.service';

@Component({
    selector: 'wf-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    providers: [DatePipe],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  userData: ManagementUser | null = null ;
  userLoggedIn: boolean = false ;
  userRole: string = '' ;
  isDistrictUser: boolean | undefined = false ;
  studentAppLink: string = '' ;
  greeting: string = 'Welcome' ;
  logoutIcon = faRightFromBracket ;
  settingsIcon = faGear ;
  demoIcon = faDesktop ;
  feedbackIcon = faComments ;
  maintenanceIcon = faClockRotateLeft;
  loginStatusSubscription: Subscription | undefined ;
  surveySubmissionSubscription: Subscription | undefined ;
  private maintenanceWarningCheckSubscription: Subscription | null = null;
  showFeedbackButton: boolean = false ;
  userSurveys: any[] = [];
  surveyIds: number[] = [] ;
  demoModeEnabled: Signal<boolean> = inject(SessionStorageService).isDemoMode ;

  // maintenance warning
  showMaintenanceWarning: boolean = false;
  maintenanceWarningTime: Date | null = null;
  private readonly MAINTENANCE_WARNING_THRESHOLD_MINUTES = 30;

  constructor(
    public loginService: LoginService,
    public sessionStorageService: SessionStorageService,
    public sidebarService: SidebarService,
    public surveyService: SurveyService,
    private sessionTimerService: SessionTimerService,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private router: Router,
  ) { }

  ngOnInit(): void {

    this.loginStatusSubscription = this.loginService.loginStatus$.subscribe((loggedIn) => {
      this.userLoggedIn = loggedIn ;
      if (loggedIn)
      {
        this.userData = this.sessionStorageService.userData() ;
        this.userRole = this.userData?.userRole ?? '' ;
        this.isDistrictUser = this.userData?.isDistrictUser() ;

        let ssoInfo: SSOCredentials | null = this.sessionStorageService.getSSOInfo() ;
        let ssoBearerToken: string | undefined = ssoInfo?.sourceToken;
        if (ssoInfo?.enabled && ssoBearerToken)
        {
          if (ssoInfo?.provider.toLocaleLowerCase() === 'clever')
          {
            this.studentAppLink = environment.studentSiteURL + '/clever/login?token=' + ssoBearerToken ;
          }
          else if (ssoInfo?.provider.toLocaleLowerCase() === 'classlink')
          {
            this.studentAppLink = environment.studentSiteURL + '/classlink/login?token=' + ssoBearerToken ;
          }
        }

        if (this.userData)
        {
          this.getSurveyData(this.userData.userID);
        }

        this.startMaintenanceWarningChecks();
      } else {
        this.stopMaintenanceWarningChecks();
      }
    }) ;

    this.surveySubmissionSubscription = this.surveyService.surveySubmission$.subscribe({
      next: (completed) => {
        if (completed && this.userData)
        {
          this.getSurveyData(this.userData?.userID) ;
        }
      }
    })
  }

  ngOnDestroy(): void {
    this.loginStatusSubscription?.unsubscribe() ;
    this.surveySubmissionSubscription?.unsubscribe() ;

    this.stopMaintenanceWarningChecks();
  }

  private startMaintenanceWarningChecks(): void {
    this.stopMaintenanceWarningChecks();

    // Check immediately once
    this.checkMaintenanceWarning();

    // Set up interval to check periodically
    const checkIntervalMillis = 10 * 1000; // 10 seconds
    this.maintenanceWarningCheckSubscription = interval(checkIntervalMillis).subscribe(() => {
        this.checkMaintenanceWarning();
    });
  }

  private stopMaintenanceWarningChecks(): void {
    this.maintenanceWarningCheckSubscription?.unsubscribe();
    this.maintenanceWarningCheckSubscription = null;

    if (this.showMaintenanceWarning) {
        this.showMaintenanceWarning = false;
        this.maintenanceWarningTime = null;
        this.changeDetectorRef.markForCheck();
    }
  }

  private checkMaintenanceWarning(): void {
    const scheduledTime = this.sessionTimerService.getScheduledMaintenanceTime();
    let shouldShowWarning = false;
    let newWarningTime: Date | null = null;

    if (scheduledTime && Date.now() >= scheduledTime.getTime()) {
      this.sessionTimerService.triggerLogout(LogoutReason.MAINTENANCE_SCHEDULED);
    }

    if (scheduledTime) {
        const now = Date.now();
        const thresholdMillis = this.MAINTENANCE_WARNING_THRESHOLD_MINUTES * 60 * 1000;
        const timeDifference = scheduledTime.getTime() - now;

        // Show warning if maintenance is scheduled, and within the threshold of the scheduled time
        if (timeDifference > 0 && timeDifference <= thresholdMillis) {
            shouldShowWarning = true;
            newWarningTime = scheduledTime;
        }
    }

    if (this.showMaintenanceWarning !== shouldShowWarning || this.maintenanceWarningTime?.getTime() !== newWarningTime?.getTime()) {
        this.showMaintenanceWarning = shouldShowWarning;
        this.maintenanceWarningTime = newWarningTime;
        this.changeDetectorRef.markForCheck();
    }
  }

  getSurveyData(userId: number): void {
    this.surveyService.getAvailableSurveys().pipe(
      concatMap((surveyIds) => {
        return this.surveyService.getUserSurveys(userId).pipe(
          switchMap((surveys) => {
            return of({
              surveyIds,
              surveys,
            }) ;
          })
        );
      })
    ).subscribe({
      next: (surveyData) => {
        this.userSurveys = surveyData.surveys ;
        this.surveyIds = surveyData.surveyIds ;
        this.showFeedbackButton = this.showFeedbackPrompt() ;
      }
    }) ;
  }

  showFeedbackPrompt(): boolean {
    if (this.userData?.isFILUser() || !this.surveyIds.length) {
      return false;
    }
    const now = new Date();
    const startWindow = new Date(now.getFullYear(), 3, 15);
    const endWindow = new Date(now.getFullYear(), 11, 15);
    const lastSurveyId = this.surveyIds[this.surveyIds.length - 1];
    const lastSurveyResponses = (this.userSurveys || []).filter(userSurvey => userSurvey.SurveyID === lastSurveyId) ;

    const isNowInWindow = now >= startWindow && now <= endWindow;

    if (!lastSurveyResponses.length) {
      return (this.userData?.numberOfUserLogins ?? 0) >= 5;
    } else {
      const latestSurvey = lastSurveyResponses[lastSurveyResponses.length - 1] ;
      const latestSurveyDate = new Date(latestSurvey.DateTaken[0], latestSurvey.DateTaken[1] - 1, latestSurvey.DateTaken[2], latestSurvey.DateTaken[3], latestSurvey.DateTaken[4], latestSurvey.DateTaken[5]);
      const isLatestSurveyInWindow = latestSurveyDate >= startWindow && latestSurveyDate <= endWindow;

      return isNowInWindow !== isLatestSurveyInWindow;
    }
  }


  logout() {
    this.sessionStorageService.setIsDemoModeEnabled(false);
    this.loginService.logout() ;
  }
  goToFeedback() {
    this.router.navigateByUrl('survey');
  }
}
